import {
  IResourceComponentsProps,
  useApiUrl,
  useNotification,
  usePermissions,
  useShow,
} from "@pankod/refine-core";
import {
  Alert,
  Card,
  Descriptions,
  Icons,
  Modal,
  Show,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Button,
} from "@pankod/refine-antd";
import { useState } from "react";

import { ILegacyConsumerLog } from "interfaces";
import { UserRole, itemOutcomeStatus, sendOutcomeStatus } from "enums";
// Mapa de rótulo do status já existe na tela de Orçamentos — reusar evita
// sinonimizar "Status SIGO" entre telas (regra de vocabulário do design system).
import { getBudgetStatusLabel } from "pages/budgets/list.helpers";
import { dateFormater } from "utils/date-formater";
import { axiosInstance } from "services/axios";
import { UpdateBudgetStatusAction } from "pages/budgets/components/update-budget-status-action";
import {
  acceptedRatio,
  buildItemRows,
  formatDurationSeconds,
  metricNumber,
} from "./aggregations";
import { FailedBudgetsTable } from "./list";

const { Text } = Typography;

export const LegacySendsShow: React.FC<IResourceComponentsProps> = () => {
  const permissions = usePermissions();
  const isAdmin = permissions.data === UserRole.Admin;
  const apiUrl = useApiUrl();
  const toast = useNotification();

  const { queryResult } = useShow<ILegacyConsumerLog>();
  const record = queryResult.data?.data;
  const isLoading = queryResult.isLoading;

  const [reprocessing, setReprocessing] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);

  const outcome = sendOutcomeStatus(record?.outcome);
  const itemRows = buildItemRows(record?.items);

  async function reprocess() {
    if (!record) {
      return;
    }
    setReprocessing(true);
    setDispatchError(null);
    try {
      await axiosInstance.post(`${apiUrl}/v1/legacy-consumer-log/reprocess`, {
        budget_id: record.budget_id,
        control_number: record.control_number,
      });
      toast.open?.({
        message: "Sucesso",
        description: "Orçamento enviado para reprocessamento.",
        type: "success",
      });
    } catch (error: any) {
      const stage = error?.response?.data?.stage;
      setDispatchError(
        stage
          ? `Falha no reprocessamento — etapa: ${stage}`
          : "Não foi possível reprocessar o orçamento. Tente novamente."
      );
    } finally {
      setReprocessing(false);
    }
  }

  function confirmReprocess() {
    Modal.confirm({
      title: "Reprocessar orçamento por completo?",
      icon: <Icons.ExclamationCircleOutlined />,
      content: `O orçamento ${
        record?.control_number ?? ""
      } entrará na fila de reconciliação. O sistema vai consultar o status no SIGO e atualizar o orçamento e a indicação. Os dados do beneficiário não são reenviados para o sistema legado.`,
      okText: "Reprocessar",
      cancelText: "Cancelar",
      onOk: reprocess,
    });
  }

  return (
    <Show
      title="Envio ao Legado"
      isLoading={isLoading}
      headerButtons={
        isAdmin ? (
          <Space>
            {/* Reuso do componente da tela de Orçamentos: reconcilia **só** o status, síncrono e
                sem modal (é idempotente). Escopo diferente do Reprocessar Completo — RN18. */}
            <UpdateBudgetStatusAction
              controlNumber={record?.control_number ?? undefined}
              onSuccess={() => queryResult.refetch()}
            />
            <Button
              type="primary"
              loading={reprocessing}
              icon={<Icons.ReloadOutlined />}
              onClick={confirmReprocess}
            >
              Reprocessar Completo
            </Button>
          </Space>
        ) : undefined
      }
    >
      {dispatchError && (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setDispatchError(null)}
          message={dispatchError}
          description="Nenhuma alteração foi enviada ao sistema legado."
        />
      )}

      {record?.outcome === "error" && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Erro no envio"
          description={
            record?.error_type
              ? `O envio não foi concluído (${record.error_type}).`
              : "O envio não foi concluído."
          }
        />
      )}

      <Card title="Resumo do Envio" style={{ marginBottom: 16 }}>
        <Descriptions bordered column={{ xs: 1, sm: 2, lg: 3 }}>
          <Descriptions.Item label="Nº de Controle">
            {record?.control_number || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Data/Hora">
            {dateFormater(record?.started_at ?? undefined) ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Situação">
            <Tag color={outcome.color}>{outcome.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status SIGO">
            {typeof record?.status_sigo === "number"
              ? getBudgetStatusLabel(record.status_sigo, "—")
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Duração">
            {formatDurationSeconds(record?.duration_seconds)}
          </Descriptions.Item>
          <Descriptions.Item label="Dependentes">
            {metricNumber(record?.dependents_count)}
          </Descriptions.Item>
          <Descriptions.Item label="Itens Aceitos">
            {acceptedRatio(record)}
          </Descriptions.Item>
        </Descriptions>

        {/* Ponte com o ELK/Datadog da spec 037 — só para Admin, discreto (RN10). */}
        {isAdmin && record?.run_id && (
          <Text type="secondary" style={{ display: "block", marginTop: 12, fontSize: 11 }}>
            Identificador da execução: {record.run_id}
          </Text>
        )}
      </Card>

      {record?.kind === "reconciliation_run" ? (
        <Card title="Orçamentos com Falha">
          <FailedBudgetsTable record={record} />
        </Card>
      ) : (
        <Card title="Itens Enviados">
          <Table
            dataSource={itemRows}
            rowKey="key"
            pagination={false}
            size="small"
            locale={{ emptyText: "Nenhum item foi enviado ao sistema legado." }}
          >
            <Table.Column dataIndex="beneficiary" title="Beneficiário" />
            <Table.Column dataIndex="document" title="Documento" />
            <Table.Column
              dataIndex="outcome"
              title="Situação"
              render={(value, row: any) => {
                const status = itemOutcomeStatus(value);
                const tag = <Tag color={status.color}>{status.label}</Tag>;
                // `http_status` é jargão: fica em tooltip e só para Admin.
                if (isAdmin && typeof row.httpStatus === "number") {
                  return (
                    <Tooltip title={`Resposta do legado: ${row.httpStatus}`}>{tag}</Tooltip>
                  );
                }
                return tag;
              }}
            />
            <Table.Column dataIndex="reason" title="Motivo" />
          </Table>
        </Card>
      )}
    </Show>
  );
};
